<template>
    <v-dialog v-model="dialog" max-width="720" persistent scrollable>
        <v-card>
            <v-toolbar dark color="primary" dense flat>
                <v-toolbar-title>
                    {{ emAndamento ? 'Movendo instâncias' : 'Mover instâncias' }}
                </v-toolbar-title>
                <v-spacer/>
                <v-btn icon :disabled="emAndamento" @click="fechar">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
                <div v-if="!lote">
                    <p class="body-2">
                        <strong>{{ instancias.length }}</strong> instância(s) sairão de
                        {{ origens.length }} pod(s): {{ origens.join(', ') }}.
                    </p>

                    <v-radio-group v-model="modo" row dense hide-details class="mt-0 mb-3">
                        <v-radio label="Um pod de destino" value="unico"/>
                        <v-radio label="Distribuir entre vários" value="auto"/>
                    </v-radio-group>

                    <v-select
                        v-if="modo === 'unico'"
                        v-model="destinoPodId"
                        :items="opcoesPod"
                        label="Pod de destino"
                        outlined
                        dense
                        prepend-inner-icon="mdi-server"
                    />

                    <v-select
                        v-else
                        v-model="autoPodIds"
                        :items="opcoesPod"
                        label="Pods que vão receber"
                        outlined
                        dense
                        multiple
                        chips
                        small-chips
                        prepend-inner-icon="mdi-server"
                    />

                    <div v-if="previa.length" class="mb-3">
                        <div class="caption grey--text mb-1">Ficaria assim:</div>
                        <v-chip
                            v-for="linha in previa"
                            :key="linha.endereco"
                            small
                            label
                            class="mr-1 mb-1"
                        >
                            {{ linha.nome }}: +{{ linha.total }}
                            <span v-if="linha.lotacao !== null" class="ml-1 grey--text">
                                ({{ linha.lotacao }} → {{ linha.lotacao + linha.total }})
                            </span>
                        </v-chip>
                    </div>

                    <v-slider
                        v-model="espaco"
                        label="Intervalo entre movimentos"
                        min="1"
                        max="30"
                        thumb-label
                        dense
                        hide-details
                        class="mb-4"
                    >
                        <template v-slot:append>
                            <span class="body-2">{{ espaco }}s</span>
                        </template>
                    </v-slider>

                    <v-alert v-for="aviso in avisos" :key="aviso.texto" dense text :type="aviso.tipo" class="body-2">
                        {{ aviso.texto }}
                    </v-alert>

                    <v-checkbox
                        v-model="forcar"
                        dense
                        hide-details
                        class="mt-2"
                        label="Forçar mesmo com campanha em execução"
                    />
                    <div class="caption grey--text ml-8 mb-2">
                        A campanha que estiver rodando na origem morre no meio — as mensagens que
                        faltavam não são enviadas.
                    </div>

                    <v-checkbox
                        v-model="assumirOrigemParada"
                        dense
                        hide-details
                        label="Assumir que o pod de origem está parado"
                    />
                    <div class="caption ml-8" :class="assumirOrigemParada ? 'red--text' : 'grey--text'">
                        Pula o descarregamento. Só use com o container comprovadamente fora do ar:
                        se ele estiver vivo, os dois pods ficam com a mesma credencial e o WhatsApp
                        derruba a sessão — o lojista precisa ler QR de novo.
                    </div>
                </div>

                <!-- DURANTE / DEPOIS: progresso do lote -->
                <div v-else>
                    <v-progress-linear
                        :value="progresso"
                        :indeterminate="emAndamento && !progresso"
                        height="18"
                        color="primary"
                        class="mb-3"
                    >
                        <span class="caption white--text">{{ concluidas }} / {{ movimentos.length }}</span>
                    </v-progress-linear>

                    <div class="mb-3">
                        <v-chip
                            v-for="(total, status) in resumo"
                            :key="status"
                            small
                            label
                            dark
                            class="mr-1"
                            :color="statusMove(status).cor"
                        >
                            {{ statusMove(status).texto }}: {{ total }}
                        </v-chip>
                    </div>

                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>Empresa</th>
                                <th>Destino</th>
                                <th>Situação</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="m in movimentos" :key="m.id">
                                <td class="body-2">
                                    {{ (m.empresa && m.empresa.fantasia) || m.empresa_id || '—' }}
                                </td>
                                <td class="body-2">{{ m.destino_host }}:{{ m.destino_porta }}</td>
                                <td>
                                    <v-chip x-small label dark :color="statusMove(m.status).cor">
                                        {{ statusMove(m.status).texto }}
                                    </v-chip>
                                    <span v-if="m.status === 'em_andamento'" class="caption grey--text ml-1">
                                        {{ etapa(m.etapa) }}
                                    </span>
                                    <div v-if="m.erro" class="caption red--text">{{ m.erro }}</div>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </div>
            </v-card-text>

            <v-card-actions>
                <v-btn v-if="lote && emAndamento" color="red" text @click="abortar">
                    Abortar o que falta
                </v-btn>

                <v-spacer/>

                <v-btn text :disabled="emAndamento" @click="fechar">
                    {{ lote ? 'Fechar' : 'Cancelar' }}
                </v-btn>

                <v-btn
                    v-if="!lote"
                    color="primary"
                    :loading="enviando"
                    :disabled="!destinoEscolhido"
                    @click="mover"
                >
                    Mover
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { ativos, carregarPods } from './pods';
import { statusMove, etapa } from './situacoes';

export default {
    name: 'MoverDialog',

    data: () => ({
        dialog: false,
        enviando: false,
        instancias: [],
        modo: 'unico',
        destinoPodId: null,
        autoPodIds: [],
        espaco: 3,
        forcar: false,
        assumirOrigemParada: false,
        lote: null,
        movimentos: [],
        resumo: {},
        timer: null,
    }),

    beforeDestroy() {
        clearInterval(this.timer);
    },

    computed: {
        origens() {
            return [...new Set(this.instancias.map(i => i.endereco))];
        },

        opcoesPod() {
            return ativos().map(p => ({
                value: p.id,
                text: p.na_memoria === null
                    ? `${p.nome} — sem resposta`
                    : `${p.nome} — ${p.na_memoria} em memória`,
                disabled: p.respondeu === false,
            }));
        },

        destinoEscolhido() {
            return this.modo === 'unico' ? !!this.destinoPodId : this.autoPodIds.length > 0;
        },

        destinos() {
            const ids = this.modo === 'unico' ? [this.destinoPodId] : this.autoPodIds;
            return ativos().filter(p => ids.includes(p.id));
        },

        previa() {
            if (!this.destinos.length) return [];

            const porPod = {};

            this.instancias.forEach((instancia, i) => {
                const pod = this.destinos[i % this.destinos.length];

                if (instancia.endereco === pod.endereco) return; // já está lá

                porPod[pod.endereco] = porPod[pod.endereco] || {
                    endereco: pod.endereco,
                    nome: pod.nome,
                    lotacao: pod.na_memoria === null ? null : pod.na_memoria,
                    total: 0,
                };

                porPod[pod.endereco].total++;
            });

            return Object.values(porPod);
        },

        avisos() {
            const lista = [];

            const comCampanha = this.instancias.filter(i => i.campanhas > 0).length;
            if (comCampanha) {
                lista.push({
                    tipo: 'warning',
                    texto: `${comCampanha} instância(s) com campanha em execução. Sem marcar "forçar", elas serão recusadas.`,
                });
            }

            const efemeras = this.instancias.filter(i => i.sessao_efemera).length;
            if (efemeras) {
                lista.push({
                    tipo: 'warning',
                    texto: `${efemeras} instância(s) são de empresa sem assinatura: a sessão será descartada no próximo restart do container, movida ou não.`,
                });
            }

            const divergentes = this.instancias.filter(i => i.situacao === 'divergente').length;
            if (divergentes) {
                lista.push({
                    tipo: 'info',
                    texto: `${divergentes} instância(s) estão vivas em um pod diferente do que o banco diz. O descarregamento vai bater no pod do banco, não onde a sessão está — considere reconciliar antes.`,
                });
            }

            const semResposta = this.instancias.filter(i => i.situacao === 'pod_sem_resposta').length;
            if (semResposta) {
                lista.push({
                    tipo: 'error',
                    texto: `${semResposta} instância(s) estão em pod que não respondeu. O movimento vai falhar no descarregamento, a menos que o pod esteja mesmo parado.`,
                });
            }

            const browsers = new Set(
                this.destinos
                    .filter(p => p.info && p.info.browser)
                    .map(p => p.info.browser.join(' / '))
            );
            if (browsers.size > 1) {
                lista.push({
                    tipo: 'info',
                    texto: `Os pods de destino usam assinaturas de browser diferentes (${[...browsers].join(' · ')}).`,
                });
            }

            return lista;
        },

        concluidas() {
            return this.movimentos.filter(m => !['pendente', 'em_andamento'].includes(m.status)).length;
        },

        progresso() {
            if (!this.movimentos.length) return 0;
            return Math.round((this.concluidas / this.movimentos.length) * 100);
        },

        emAndamento() {
            return !!this.lote && this.concluidas < this.movimentos.length;
        },
    },

    methods: {
        statusMove,
        etapa,

        async abrir(instancias) {
            this.instancias = instancias;
            this.modo = 'unico';
            this.destinoPodId = null;
            this.autoPodIds = [];
            this.forcar = false;
            this.assumirOrigemParada = false;
            this.lote = null;
            this.movimentos = [];
            this.resumo = {};
            this.dialog = true;

            try {
                await carregarPods(this.$http, true, true);
            } catch (e) {
                this.notify('Não foi possível ler o estado dos pods', 'error');
            }
        },

        fechar() {
            clearInterval(this.timer);
            this.dialog = false;

            if (this.lote) {
                this.$emit('concluido');
            }
        },

        async mover() {
            const destinoTexto = this.modo === 'unico'
                ? this.destinos.map(p => p.nome).join('')
                : `${this.destinos.length} pods`;

            const ok = await this.$root.$confirm(
                'Mover instâncias',
                `${this.instancias.length} sessão(ões) serão descarregadas do pod atual e carregadas em `
                + `${destinoTexto}. Cada uma fica alguns segundos sem atender.`
            );

            if (!ok) return;

            this.enviando = true;

            try {
                const { data } = await this.$http.post('instancias-whatsapp/mover', {
                    instancia_ids: this.instancias.map(i => i.id),
                    destino_pod_id: this.modo === 'unico' ? this.destinoPodId : null,
                    auto_pod_ids: this.modo === 'auto' ? this.autoPodIds : null,
                    forcar: this.forcar,
                    assumir_origem_parada: this.assumirOrigemParada,
                    espaco_segundos: this.espaco,
                });

                if (data.type !== 'success') {
                    this.notify(data.msg || 'Não foi possível iniciar o lote', 'warning');
                    this.enviando = false;
                    return;
                }

                if (data.data.ignorados && data.data.ignorados.length) {
                    this.notify(`${data.data.ignorados.length} instância(s) já estavam no destino`, 'info');
                }

                this.lote = data.data.lote_uuid;
                this.acompanhar();
            } catch (e) {
                this.notify('Não foi possível iniciar o lote', 'error');
            }

            this.enviando = false;
        },

        acompanhar() {
            clearInterval(this.timer);

            const ler = async () => {
                try {
                    const { data } = await this.$http.get('instancias-whatsapp/movimentacoes', {
                        params: { lote: this.lote, rows: 200 },
                    });

                    this.movimentos = data.data || [];
                    this.resumo = data.resumo || {};

                    if (!this.emAndamento) {
                        clearInterval(this.timer);
                    }
                } catch (e) {
                    clearInterval(this.timer);
                }
            };

            ler();
            this.timer = setInterval(ler, 3000);
        },

        async abortar() {
            const ok = await this.$root.$confirm(
                'Abortar lote',
                'O que ainda não começou é cancelado. O movimento que já está em andamento termina — '
                + 'interromper no meio é o que deixaria dois pods com a mesma sessão.'
            );

            if (!ok) return;

            try {
                await this.$http.post('instancias-whatsapp/abortar-lote', { lote: this.lote });
                this.notify('Lote abortado', 'success');
            } catch (e) {
                this.notify('Não foi possível abortar', 'error');
            }
        },
    },
};
</script>
